import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Contact } from './contact.model';
import { HsContact, HsContactDataSource } from '@hsetu/ngui-views';
import { inject } from '@angular/core';
import { ContactService } from '../services/contact.service';
import { SortType } from '@shared/enums/sort-type.enum';
import { ContactMapper } from '../mappers/contact.mapper';
import { FilterBy } from '@shared/sync/filter-by.model';

export class ContactDataSource {
  private readonly _contactService = inject(ContactService);

  private readonly _alphabet: string[] = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    '#',
  ];

  private _fnContacts = async (params: {
    letter: string;
    pageSize: number;
    pageIndex: number;
    filter?: string | undefined;
    searchTerm?: string | undefined;
  }) => {
    const sort: SortBy = {
      sort: SortType.ASC,
      property: 'lastName',
    };

    let result: IPaginatedItemsModel<Contact> | undefined = undefined;

    if (params.searchTerm && params.searchTerm.length > 0) {
      const filterBy: FilterBy = {
        filterStatement: `return i.lastName !== undefined && i.lastName.toLowerCase().includes(p[0].toLowerCase()) 
          || i.firstName !== undefined && i.firstName.toLowerCase().includes(p[0].toLowerCase()) 
          || i.companyName !== undefined && i.companyName.toLowerCase().includes(p[0].toLowerCase())`,
        parameters: [params.searchTerm],
      };

      result = await this._contactService.getAll(
        params.pageSize,
        params.pageIndex,
        sort,
        filterBy,
      );
    } else {
      result = await this._contactService.getContactsByLetter(
        params.letter,
        params.pageSize,
        params.pageIndex,
        sort,
      );
    }

    const contacts = result.data.map((contact: Contact) => {
      const mappedContact: HsContact = this._mapper.mapToHsContact(contact);

      return mappedContact;
    });

    return {
      contacts: contacts,
      maxCount: result.count,
    };
  };

  private _letters: { letter: string; isRead: boolean }[] = [];

  private readonly _dataSource: HsContactDataSource;
  private readonly _mapper: ContactMapper;

  public get dataSource(): HsContactDataSource {
    return this._dataSource;
  }

  public async reloadLetters(
    preSelectLetter?: string,
  ): Promise<{ letter: string; isRead: boolean }[]> {
    const letters = await this._contactService.getLetters(this._alphabet);
    this._letters = letters;
    const filtered = await this._filterTenantOnlyLetters(letters);
    const sorted = [...filtered].sort((a, b) => a.letter.localeCompare(b.letter));

    if (preSelectLetter) {
      // Set the target letter BEFORE setAvailableLetters so the auto-effect in
      // HsContactBook sees a non-undefined currentLetter and skips auto-selecting
      // the first letter.
      const matched = sorted.find((l) => l.letter === preSelectLetter);
      if (matched) {
        this._dataSource.setLetter(matched);
      }
    }

    this._dataSource.setAvailableLetters(sorted);
    return sorted;
  }

  public async navigateToLetter(targetLetter: string): Promise<void> {
    const letters = await this.reloadLetters(targetLetter);
    const matched = letters.find((l) => l.letter === targetLetter);
    if (matched) {
      await this._dataSource.load();
    }
  }

  private async _filterTenantOnlyLetters(
    letters: { letter: string; isRead: boolean }[],
  ): Promise<{ letter: string; isRead: boolean }[]> {
    const results = await Promise.all(
      letters.map(async (l) => {
        const page = await this._contactService.getContactsByLetter(l.letter, 1, 0);
        return page.count > 0 ? l : null;
      }),
    );
    return results.filter(
      (l): l is { letter: string; isRead: boolean } => l !== null,
    );
  }

  constructor() {
    this._mapper = new ContactMapper();

    this._dataSource = new HsContactDataSource(
      this._fnContacts.bind(this),
      this._letters,
      0,
      5,
    );

    this.reloadLetters();
  }
}
